/**
 * RecordMetaSection — the read-only metadata block in a tabbed slideout's top
 * zone (per the automation design): Created by / Created / Updated, in
 * OverviewFieldRow geometry, above the tab bar with a divider between them.
 *
 * Created-by is sourced from the `ahoy_entity_to_creator` association (the
 * CRM's own creation provenance edge — any → principal) when the record
 * carries it, rendered as the canonical entity badge. Records that predate
 * the edge (imports, legacy rows) fall back to deriving it from the
 * `ahoy_object_created` timeline event's actor: one server-side filtered
 * fetch, resolved through the timeline's own formatter, which handles users,
 * agents and entity actors alike.
 */

import { Calendar, Clock, User01 } from '@untitledui/icons';
import { useMemo } from 'react';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { formatWithSafeTimeZone } from '@/lib/time';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { OverviewFieldRow } from '../components/OverviewFieldRow';
import { ActorName } from '../timeline/components/shared/ActorName';
import {
  emptyViewStateLookups,
  ViewStateLookupsProvider,
} from '../timeline/context/TimelineLookupsContext';
import type { TimelineFilters } from '../timeline/types';
import { useEventFormatters } from '../timeline/useEventFormatters';
import {
  mergeLookups,
  useTimelineViewState,
} from '../timeline/useTimelineViewState';

const CREATION_EVENT_FILTER: TimelineFilters = {
  eventTypes: ['ahoy_object_created'],
  startDate: null,
  endDate: null,
  actorId: null,
  includeAssociated: false,
};

/** Must render INSIDE ViewStateLookupsProvider — getActorInfo reads it. */
function CreatorName({ event }: { event: { event_id: string } }) {
  const { getActorInfo } = useEventFormatters();
  // The formatter takes the raw event; the narrow structural type above is
  // just what this file needs to name.
  const actor = getActorInfo(event as Parameters<typeof getActorInfo>[0]);
  return <ActorName actor={actor} />;
}

function CreatedByValue({ object }: { object: CrmObject }) {
  const { data } = useTimelineViewState({
    entityId: object.entity_id,
    orgId: String(object.org_id),
    entityType: object.entity_type_name,
    filters: CREATION_EVENT_FILTER,
  });
  const creationEvent = useMemo(
    () =>
      data?.pages
        .flatMap(page => page.events)
        .find(e => e.event_type_name === 'ahoy_object_created'),
    [data?.pages]
  );
  const lookups = useMemo(
    () => (data?.pages ? mergeLookups(data.pages) : emptyViewStateLookups),
    [data?.pages]
  );

  if (!creationEvent) {
    // Loading, or genuinely unknown (imported records can predate the event
    // log). A dash is honest; a spinner for one name is noise.
    return <span className='text-xs text-fg-tertiary'>—</span>;
  }
  return (
    <ViewStateLookupsProvider lookups={lookups}>
      <span className='text-xs md:text-sm text-fg-primary'>
        <CreatorName event={creationEvent} />
      </span>
    </ViewStateLookupsProvider>
  );
}

/**
 * The creation provenance edge, stamped by the CRM at create time on every
 * entity (see association_types.yaml `ahoy_entity_to_creator`).
 */
const DEFAULT_CREATOR_ASSOCIATION_TYPES = ['ahoy_entity_to_creator'] as const;

export function RecordMetaSection({
  object,
  variant = 'rows',
  creatorAssociationTypes = DEFAULT_CREATOR_ASSOCIATION_TYPES,
}: {
  object: CrmObject;
  variant?: 'rows' | 'inline';
  creatorAssociationTypes?: readonly string[];
}) {
  const { schema } = usePropertySchema(object.org_id, object.entity_type_name);
  const displayTimeZone = useDisplayTimezone();
  const createdAt = getPropertyValue<string>(
    object.properties?.ahoy_created_at
  );
  const updatedAt = getPropertyValue<string>(
    object.properties?.ahoy_updated_at
  );
  // The stored creator edge beats the derived timeline actor: it survives
  // event-log truncation and needs no extra fetch. many_to_one — at most one.
  const creatorEntityId = creatorAssociationTypes
    .map(
      t =>
        object.associations?.find(a => a.association_type_name === t)
          ?.target_entity_id
    )
    .find(Boolean);

  if (variant === 'inline') {
    const createdOn = createdAt
      ? formatWithSafeTimeZone(
          new Date(createdAt),
          { month: 'short', day: 'numeric', year: 'numeric' },
          { timeZone: displayTimeZone }
        )
      : '';
    return (
      <div className='flex flex-wrap items-center gap-1 pt-2 text-xs md:text-sm text-fg-tertiary'>
        <span>Created by</span>
        {creatorEntityId ? (
          <EntityBadgeById
            entityId={creatorEntityId}
            orgId={object.org_id}
            variant='inline'
          />
        ) : (
          <CreatedByValue object={object} />
        )}
        {createdOn && <span>on {createdOn}</span>}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-1 pb-4'>
      <OverviewFieldRow icon={User01} label='Created by'>
        {creatorEntityId ? (
          <EntityBadgeById
            entityId={creatorEntityId}
            orgId={object.org_id}
            variant='xs'
          />
        ) : (
          <CreatedByValue object={object} />
        )}
      </OverviewFieldRow>
      {createdAt && (
        <OverviewFieldRow icon={Calendar} label='Created'>
          <PropertyValueLabel
            value={createdAt}
            propertyType={schema['ahoy_created_at']}
            orgId={object.org_id}
            fontSize='xs'
          />
        </OverviewFieldRow>
      )}
      {updatedAt && (
        <OverviewFieldRow icon={Clock} label='Updated'>
          <PropertyValueLabel
            value={updatedAt}
            propertyType={schema['ahoy_updated_at']}
            orgId={object.org_id}
            fontSize='xs'
          />
        </OverviewFieldRow>
      )}
    </div>
  );
}
